import {
  type HighlightIndex,
  type HighlightSentiment,
  lookupHighlight,
} from "./buildKeyMessageHighlightIndex";

/**
 * Walk the rendered response HTML and wrap text spans that match a key-message
 * snippet with a styled `<mark>`. Browser-only (uses `DOMParser`).
 *
 * Granularity: matches whole text nodes whose normalized content contains a
 * normalized snippet. A snippet that markdown formatting split across multiple
 * nodes (e.g. inline `**bold**` inside the sentence) won't match — that is an
 * accepted limitation for the inline-highlight feature, which is a *teaser*
 * for the dashboard's awareness segment, not a comprehensive view.
 *
 * Returns the input untouched when running on the server, when the index is
 * empty, or when no matches are found.
 */
export function applyKeyMessageHighlights(
  htmlContent: string,
  platform: string,
  index: HighlightIndex,
): string {
  if (typeof window === "undefined") return htmlContent;
  if (!htmlContent || index.size === 0) return htmlContent;

  const doc = new DOMParser().parseFromString(
    `<div id="__root__">${htmlContent}</div>`,
    "text/html",
  );
  const root = doc.body.querySelector("#__root__");
  if (!root) return htmlContent;

  let mutated = false;

  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  let current: Node | null = walker.nextNode();
  while (current) {
    textNodes.push(current as Text);
    current = walker.nextNode();
  }

  for (const node of textNodes) {
    // Skip text already inside a mark we added — avoids nesting if the same
    // snippet matches twice in a single node.
    if (node.parentElement?.classList.contains("__km-highlight")) continue;

    const raw = node.textContent ?? "";
    if (raw.trim().length === 0) continue;

    // Try every sentence boundary in this text node — split on terminal
    // punctuation so a paragraph with many sentences still lights up only
    // the ones that match.
    const segments = splitIntoSentenceCandidates(raw);
    if (segments.length === 0) continue;
    let replacementHtml = "";
    let didReplace = false;
    for (const seg of segments) {
      const entry = lookupHighlight(index, platform, seg.text);
      if (entry) {
        replacementHtml += seg.leadingWs;
        replacementHtml += renderHighlightSpan(seg.text, entry);
        replacementHtml += seg.trailingPunct;
        didReplace = true;
      } else {
        replacementHtml += seg.leadingWs + escapeHtml(seg.text) + seg.trailingPunct;
      }
    }

    if (didReplace) {
      const tmp = doc.createElement("span");
      tmp.innerHTML = replacementHtml;
      const parent = node.parentNode;
      if (parent) {
        while (tmp.firstChild) {
          parent.insertBefore(tmp.firstChild, node);
        }
        parent.removeChild(node);
        mutated = true;
      }
    }
  }

  return mutated ? root.innerHTML : htmlContent;
}

interface SentenceCandidate {
  leadingWs: string;
  text: string;
  trailingPunct: string;
}

/**
 * Split a text-node value into sentence candidates, preserving the leading
 * whitespace and trailing punctuation around each. Keeps round-trip fidelity
 * so the rebuilt HTML matches the original spacing.
 */
function splitIntoSentenceCandidates(raw: string): SentenceCandidate[] {
  const out: SentenceCandidate[] = [];
  const re = /([^.!?\n]+?)([.!?]+|$)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(raw)) !== null) {
    const fullStart = match.index;
    const fullEnd = re.lastIndex;
    if (fullEnd === lastIndex) break; // safety against zero-width loops

    const segmentRaw = raw.slice(fullStart, fullEnd);
    const leadingWsLen = segmentRaw.length - segmentRaw.trimStart().length;
    const leadingWs = segmentRaw.slice(0, leadingWsLen);
    const body = segmentRaw.slice(leadingWsLen);
    const trailingPunct = match[2] ?? "";
    const text = body.slice(0, body.length - trailingPunct.length).trim();
    if (text.length === 0) {
      out.push({ leadingWs, text: "", trailingPunct });
    } else {
      out.push({ leadingWs, text, trailingPunct });
    }
    lastIndex = fullEnd;
    if (match[2] === "") break;
  }
  return out;
}

function renderHighlightSpan(text: string, entry: {
  message: string;
  sentiment: HighlightSentiment;
  score: number;
}): string {
  const classes = sentimentClasses(entry.sentiment);
  const escaped = escapeHtml(text);
  const tooltip = escapeAttr(
    `Matches “${entry.message}” (${Math.round(entry.score * 100)}% similar)`,
  );
  return `<mark class="__km-highlight ${classes}" title="${tooltip}">${escaped}</mark>`;
}

function sentimentClasses(sentiment: HighlightSentiment): string {
  // Purple = positive key-message match, orange = negative. Neutral key
  // messages are not highlighted — they were tracked for the time series but
  // don't drive the buyer-narrative story the report tells.
  switch (sentiment) {
    case "POSITIVE":
      return "bg-purple-100 text-purple-900 px-1 rounded";
    case "NEGATIVE":
      return "bg-orange-100 text-orange-900 px-1 rounded";
    default:
      return "";
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/"/g, "&quot;");
}
